import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Drawer, Stepper, useDrawer } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import * as Sentry from '@sentry/react'
import { defineStepper } from '@stepperize/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import z from 'zod'

import {
  EditNewsContentForm,
  EditNewsContentSchema,
} from '../../schemas/EditNewsContentSchema'
import { EditNewsHighlightedImageSchema } from '../../schemas/EditNewsHighlightedImageSchema'
import {
  EditNewsImageForm,
  EditNewsImageSchema,
} from '../../schemas/EditNewsImageSchema'
import { NewsCropSchema } from '../../schemas/NewsImageSchema'
import { NewsSchema } from '../../schemas/NewsSchema'
import {
  NewsSummaryForm,
  NewsSummarySchema,
} from '../../schemas/NewsSummarySchema'
import { useEditNews } from '../hooks/useEditNews'
import { useEditNewsStore } from '../stores/useEditNewsStore'
import { FormEditNewsContent } from './FormEditNewsContent'
import { FormEditNewsImage } from './FormEditNewsImage'
import { FormEditNewsSummary } from './FormEditNewsSummary'
import { FormNewsImage } from './FormNewsImage'
import { NewsPreview } from './NewsPreview'
import { ToastNewsMissingFields } from './ToastNewsMissingFields'

type Form = NewsSummaryForm & EditNewsContentForm & EditNewsImageForm

export function DrawerEditNews() {
  const { news } = useEditNewsStore()
  const { useStepper, utils } = defineStepper(
    {
      id: 'summary',
      schema: NewsSummarySchema(),
    },
    {
      id: 'content',
      schema: EditNewsContentSchema(),
    },
    {
      id: 'image',
      schema: news?.isSlider
        ? EditNewsHighlightedImageSchema()
        : EditNewsImageSchema(),
    },
    {
      id: 'crop',
      schema: NewsCropSchema(),
    },
    {
      id: 'preview',
      schema: z.object({}),
    },
  )
  const { t } = useTranslation('panel')
  const stepper = useStepper()
  const drawerEditNews = useDrawer('drawer-edit-news')
  const hookForm = useForm({
    resolver: zodResolver(stepper.current.schema),
  })
  const { handleEditNews, isEditingNews } = useEditNews()

  const handleSubmit = async () => {
    if (!news?.id) {
      Sentry.captureMessage('News ID is missing', 'warning')
      return
    }

    const data = hookForm.getValues() as Form

    if (stepper.isLast) {
      const { success } = NewsSchema.safeParse(data)

      if (!success) {
        return toast(ToastNewsMissingFields, {
          autoClose: 8000,
          data: {
            onLinkOrFile: () => stepper.goTo('summary'),
            onContent: () => stepper.goTo('content'),
          },
        })
      }

      await handleEditNews(news.id, data)
      return handleClose()
    }

    if (
      (!data?.image || (Array.isArray(data?.image) && !data?.image?.length)) &&
      stepper.current.id === 'image'
    ) {
      return stepper.goTo('preview')
    }

    return stepper.next()
  }

  const handleBack = () => {
    const data = hookForm.getValues() as Form

    if (
      (!data?.image || (Array.isArray(data?.image) && !data?.image?.length)) &&
      stepper.current.id === 'preview'
    ) {
      return stepper.goTo('image')
    }

    stepper.prev()
  }

  const handleClose = () => {
    drawerEditNews.close()
    hookForm.reset()
    stepper.reset()
  }

  const CURRENT_STEP = utils.getIndex(stepper.current.id) + 1

  useEffect(() => {
    if (!news) return

    hookForm.reset({
      title: news?.title || '',
      subtitle: news?.subTitle || '',
      tag: news?.tag?.map((tag) => ({ label: tag, value: tag })) || [],
      postExternalUrl: news.postExternalUrl || '',
      content: news.post?.content || '',
      order: news.sliderSort || 0,
      postImage: news.postImage || null,
      isSlider: news.isSlider || false,
    })
  }, [news, hookForm])

  return (
    <Drawer.Root id="drawer-edit-news">
      <Drawer.Header className="w-full flex gap-4" onClose={handleClose}>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('news.drawer_edit_news.header.title')}
        </h1>

        <Stepper.Root className="sm:min-w-sm mx-auto">
          <Stepper.StepNumber
            number={1}
            isCompleted={CURRENT_STEP > 1}
            isActive={stepper.current.id === 'summary'}
          />

          <Stepper.StepNumber
            number={2}
            isCompleted={CURRENT_STEP > 2}
            isActive={stepper.current.id === 'content'}
          />

          <Stepper.StepNumber
            number={3}
            isCompleted={CURRENT_STEP > 3}
            isActive={stepper.current.id === 'image'}
          />

          <Stepper.StepNumber
            number={4}
            isCompleted={CURRENT_STEP > 4}
            isActive={stepper.current.id === 'crop'}
          />

          <Stepper.StepNumber
            number={5}
            isActive={stepper.current.id === 'preview'}
          />
        </Stepper.Root>
      </Drawer.Header>

      <Drawer.Body>
        <FormProvider {...hookForm}>
          <form
            onSubmit={hookForm.handleSubmit(handleSubmit)}
            className="h-full"
          >
            {stepper.switch({
              summary: () => <FormEditNewsSummary />,
              content: () => <FormEditNewsContent />,
              image: () => <FormEditNewsImage />,
              crop: () => <FormNewsImage aspectRatio={3.5 / 1} />,
              preview: () => <NewsPreview />,
            })}
          </form>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer className="flex gap-4 justify-end">
        {stepper.isFirst ? (
          <Button.Root variant="secondary" onClick={handleClose}>
            {t('news.drawer_edit_news.footer.button_cancel')}
          </Button.Root>
        ) : (
          <Button.Root variant="secondary" onClick={handleBack}>
            {t('news.drawer_edit_news.footer.button_prev')}
          </Button.Root>
        )}

        <Button.Root
          onClick={hookForm.handleSubmit(handleSubmit)}
          isLoading={isEditingNews}
        >
          {stepper.isLast
            ? t('news.drawer_edit_news.footer.button_submit')
            : t('news.drawer_edit_news.footer.button_next')}
        </Button.Root>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
